import React, { useEffect, useMemo, useState } from 'react'
import { Dialog } from '../ui/dialog'
import { Switch } from '../ui/switch'
import { Button } from '../ui/button'
import { useSelector, useDispatch } from 'react-redux'
import { Filters, RecipeTrigger, TableName } from '../../config'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { FileInput, type BreadcrumbProps } from '@blueprintjs/core'
import { Menu, MenuItem } from '../ui/menu'
import { Callout } from '../ui/callout'
import { Layout } from '../Layout'
import type { LoadFunc, Params } from '../../packages/blueprint-components/src/useTableHooks'
import { UpdateReportAdTypeBadge } from '../badge/UpdateReportAdTypeBadge'
import { ActionBadge } from './Actions'
import { FrequencyBadge, MerchJarSegmentsAPI } from '../../pages/recipe_detail'
import { Toasts } from '../toaster/Toast'
import Recipe = Models.Recipe
import { axiosGet, axiosPost, axiosDestroy } from '../../util/api'
import type { EntityId } from '../../types/constants'
import { setRecipeProgress as reduxSetRecipesProgress } from '../../actions/progress'
import { useInterval } from '../../hooks/useInterval'
import { snakeCase } from 'change-case'
import { useUser } from '../../hooks/useUser'
import { Popover } from '../ui/popover'
import { fetchRecipeProgress, type State } from '../../reducers'
import { isNavV2Enabled } from '../../util/nav-version'
import { PreservingLink } from '../ui/PreservingLink'
import { EllipsisMenu } from '../../icons/icons'
import type { BulkActionProgress, RecipeProgressList } from '../../types/reducers/progress'
import { ProgressPreview } from '../BulkActionsPreviewDialog'
import type { Column } from '../../types/table'
import classNames from 'classnames'
import { CustomFieldTriggerCode } from '../../features/custom-fields'
import { useUpgradeToast } from '../../features/recipe-upgrades/upgrade-toast'
import {
    ScheduledSegmentFrequency,
    formatProfileDateTime,
} from '../../features/scheduled-segments/components/ScheduledSegmentListPresentation'
import type { ScheduledSegmentListRecipe } from '../../features/scheduled-segments/components/ScheduledSegmentListPresentation'
import { RecipeListAgGrid, useRecipeListTable } from './RecipeListAgGrid'
import { setSegmentEnabled } from './segment-enabled-toggle'
import { getSegmentSources } from '../../features/recipe-upgrades/list-client'
import type { SegmentSource } from '../../features/recipe-upgrades/list-types'

const filters = [
    Filters.RecipeEnabled,
    Filters.RecipeAdTypes,
    Filters.RecipeActions,
    Filters.RecipeFrequencies,
]

interface SegmentsTableProps {
    reloadParent?: any
    targetId?: any
    targetType?: any
    showHeader?: boolean
    nested?: boolean
}

export const SegmentsTable = ({
    reloadParent,
    showHeader = true,
    nested = false,
}: SegmentsTableProps) => {
    useUpgradeToast()
    const dispatch = useDispatch()
    const { user, activeProfile } = useUser()
    const [loading, setLoading] = useState(false)
    const [recipe, setRecipe] = useState<Models.Recipe | null>(null)
    const recipesProgress = useSelector<State, RecipeProgressList>(
        (store) => store.progress.recipes,
    )
    const [importRecipe, setImportRecipe] = useState(false)
    const profile = useSelector<State, EntityId | null>((state) => state.user.profile)
    const navigate = useNavigate()
    const { search } = useLocation()
    const useNavV2 = useMemo(() => isNavV2Enabled(search), [search])
    const useNavV2FlatChrome = useNavV2 && !nested
    const API = MerchJarSegmentsAPI
    const profileTimezone = activeProfile?.timezone ?? 'UTC'
    const [segmentSources, setSegmentSources] = useState<SegmentSource[]>([])

    const load: LoadFunc<Recipe> = (params: any) => {
        if (!profile && !user?.is_admin) {
            return Promise.resolve({
                data: {
                    data: [],
                },
            })
        }

        if (reloadParent) {
            const { from, to }: Params = { ...params }
            reloadParent({ from, to })
        }

        // Add version filter to only show version 2 recipes
        const segmentsParams = {
            ...params,
            'filter[version]': 2,
        }

        return API.Segments.Browse(segmentsParams).then(async (response: any) => {
            const ids = response.data.data.map((segment: Recipe) => String(segment.id))
            try {
                setSegmentSources(await getSegmentSources(ids))
            } catch {
                setSegmentSources([])
            }
            return response
        })
    }

    const { store, savedFiltersStore, tableHooks } = useRecipeListTable(
        TableName.Segments,
        load,
        profile,
    )

    const recipesCurrentlyRunning = useMemo(
        () => Object.keys(recipesProgress).length > 0,
        [JSON.stringify(recipesProgress)],
    )

    useEffect(() => {
        tableHooks.reload()
    }, [recipesCurrentlyRunning])

    useInterval(() => {
        // @ts-expect-error
        dispatch(fetchRecipeProgress()).then((finishedUuids: string[]) => {
            finishedUuids.forEach((uuid) => {
                axiosGet('recipes/run-info', { uuid })
                    .then((res) => {
                        Toasts.success(res.data.message)
                        console.log('segments run success', res.data)
                    })
                    .catch((err) => {
                        console.log('segments fetch error', err)
                    })
            })
        })
    }, 1000)

    const duplicateRecipe = ({ recipe }: { recipe: Models.Recipe }) => {
        axiosPost(`recipes/${recipe.id}/duplicate`)
            .then(() => {
                Toasts.success('Segment successfully duplicated')
                tableHooks.reload()
            })
            .catch(() => {
                Toasts.error('Failed to duplicate segment')
            })
    }

    const exportRecipe = ({ recipe }: { recipe: Models.Recipe }) => {
        axiosGet(`recipes/${recipe.id}/export`).then((r) => {
            // Create blob link to download
            const url = window.URL.createObjectURL(new Blob([r.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', snakeCase(recipe.name) + `.recipe`)

            // Append to html link element page
            document.body.appendChild(link)

            // Start download
            link.click()

            // Clean up and remove the link
            link.parentNode?.removeChild(link)

            Toasts.success('Segment exported')
        })
    }

    const deleteRecipe = ({ recipe }: { recipe: Models.Recipe }) => {
        axiosDestroy(`recipes/${recipe.id}`)
            .then(() => {
                Toasts.success('Segment deleted')
                tableHooks.reload()
            })
            .catch(() => {
                Toasts.error('Failed to delete segment')
            })
    }

    const columns = useMemo(() => {
        const sources = new Map(segmentSources.map((source) => [source.segment_id, source]))
        const c: Column<Recipe, any>[] = [
            {
                key: 'name',
                cell: (recipe) => {
                    const source = sources.get(String(recipe.id))
                    return <div><PreservingLink to={`/segments/${recipe?.id}`}>{recipe?.name}</PreservingLink>{source && <div className="mt-1 flex items-center gap-2 text-xs leading-4"><Popover interactionKind="hover" content={<div className="max-w-[340px] bg-[#1C2127] px-3 py-2.5 text-white">{source.source_deleted ? 'The original recipe was deleted.' : `Created from your recipe on ${source.upgraded_at ? new Date(source.upgraded_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', timeZone: 'UTC' }) : ''}. The recipe is switched off.`}</div>}><span className="rounded-sm bg-[rgba(143,153,168,.15)] px-1.5 py-0.5 text-[#5F6B7C]">{source.source_deleted ? 'Upgraded from a deleted recipe' : 'Upgraded from'}</span></Popover>{!source.source_deleted && <Link className="text-[#215DB0] hover:text-[#184A90] hover:underline focus:text-[#184A90] focus:underline" to={`/recipes/${source.source_recipe_id}`}>{source.source_name}</Link>}</div>}</div>
                },
            },
            {
                key: 'enabled',
                cell: (recipe, extra: any) => {
                    return (
                        <Switch
                            className="mb-0"
                            checked={recipe.enabled || false}
                            onChange={(e: any) => {
                                const enabled = e.target.checked
                                setSegmentEnabled(recipe, enabled, () => {
                                    extra.mutateItems((recipes: Recipe[]) =>
                                        recipes.map((r) => {
                                            if (r.id === recipe.id) {
                                                return {
                                                    ...r,
                                                    enabled,
                                                }
                                            }
                                            return r
                                        }),
                                    )
                                })
                            }}
                        />
                    )
                },
            },
            {
                key: 'ad_type',
                cell: (recipe) => {
                    return <UpdateReportAdTypeBadge type={recipe.ad_type} />
                },
            },
        ]

        c.push(
            {
                key: 'trigger',
                cell: (recipe) => {
                    return (
                        <div
                            title={recipe.trigger}
                            className="max-h-[60px] max-w-[500px] overflow-hidden"
                        >
                            <CustomFieldTriggerCode
                                code={recipe.trigger}
                                adType={recipe.ad_type}
                                profileId={profile === null ? null : String(profile)}
                            />
                        </div>
                    )
                },
            },
            {
                key: 'frequency',
                cell: (recipe) => (
                    <ScheduledSegmentFrequency
                        recipe={recipe as ScheduledSegmentListRecipe}
                        fallback={<FrequencyBadge {...recipe} />}
                    />
                ),
            },
            {
                key: 'action',
                cell: (recipe) => {
                    return (
                        <ActionBadge action={recipe.action} action_params={recipe.action_params} />
                    )
                },
            },
            {
                key: 'last_change',
                cell: (recipe) => (
                    <div>{formatProfileDateTime(recipe.last_change, profileTimezone)}</div>
                ),
            },
            {
                key: 'last_run',
                cell: (recipe) => (
                    <div>{formatProfileDateTime(recipe.last_run, profileTimezone)}</div>
                ),
            },
            {
                key: ' ',
                cell: (recipe) => {
                    return (
                        <EllipsisMenu
                            content={
                                <Menu>
                                    <Popover
                                        interactionKind="hover"
                                        content={
                                            <div className="p-2">Cannot run a disabled segment</div>
                                        }
                                        disabled={recipe?.enabled}
                                    >
                                        <MenuItem
                                            disabled={recipesCurrentlyRunning || !recipe?.enabled}
                                            text="Run Now"
                                            onClick={() => setRecipe(recipe)}
                                        />
                                    </Popover>
                                    <MenuItem
                                        text="Duplicate"
                                        onClick={() => duplicateRecipe({ recipe })}
                                    />
                                    {sources.get(String(recipe.id)) && !sources.get(String(recipe.id))?.source_deleted && <MenuItem text="View original recipe" onClick={() => navigate(`/recipes/${sources.get(String(recipe.id))?.source_recipe_id}`)} />}
                                    <MenuItem
                                        text="Export"
                                        onClick={() => exportRecipe({ recipe })}
                                    />
                                    <MenuItem
                                        text="Delete"
                                        onClick={() => {
                                            if (
                                                confirm(
                                                    `Are you sure you want to delete segment "${recipe.name}"?`,
                                                )
                                            ) {
                                                deleteRecipe({ recipe })
                                            }
                                        }}
                                    />
                                </Menu>
                            }
                        />
                    )
                },
            },
        )

        return c
    }, [navigate, profile, profileTimezone, recipesCurrentlyRunning, segmentSources])

    const breadcrumbs: BreadcrumbProps[] = [{ text: 'Automate' }]

    return (
        <Layout
            breadcrumbs={breadcrumbs}
            nested={nested}
            primary={showHeader ? 'Segments' : null}
            className={useNavV2FlatChrome ? 'bg-white' : undefined}
            layoutClassName={useNavV2FlatChrome ? '!p-0 md:!p-0 bg-white' : undefined}
            extraMenuItems={[
                <MenuItem
                    key="create"
                    icon="add"
                    text="Create Segment"
                    onClick={() => navigate('/segments/new')}
                />,
                <MenuItem
                    key="import"
                    icon="import"
                    text="Import Segment"
                    onClick={() => setImportRecipe(true)}
                />,
            ]}
        >
            <RecipeListAgGrid
                name={TableName.Segments}
                store={store}
                savedFiltersStore={savedFiltersStore}
                tableHooks={{
                    ...tableHooks,
                    params: {
                        ...tableHooks.params,
                        filter: { ...tableHooks.params.filter, version: 2 },
                    },
                }}
                filters={filters}
                columns={columns}
                nested={nested}
                fitParent={!nested}
                className={classNames(!showHeader && !useNavV2FlatChrome && 'mt-0 mb-4')}
                cardClassName={classNames(
                    'table-header-top-border',
                    useNavV2FlatChrome && '!rounded-none !shadow-none',
                )}
                createAction={
                    <Button
                        intent="primary"
                        text="Create Segment"
                        className="mr-2 rounded-lg whitespace-nowrap"
                        onClick={() => navigate('/segments/new')}
                    />
                }
            />
            <RecipesProgressPreviewDialog
                isOpen={recipesCurrentlyRunning}
                recipes={tableHooks.items}
                data={recipesProgress}
            />
            <RecipeImportModal
                isOpen={importRecipe}
                setIsOpen={setImportRecipe}
                refresh={tableHooks.reload}
            />
            {/* nonModal: contains a hover Popover that portals to body; modal
                mode disables pointer events on body and breaks the popover. */}
            <Dialog isOpen={!!recipe} onClose={() => setRecipe(null)} className="p-4" nonModal>
                <span>
                    Are you sure you want to run {recipe?.name}? This segment
                    {recipe?.last_run == null
                        ? ' has never been run'
                        : ` was last run at ${formatProfileDateTime(recipe.last_run, profileTimezone)}`}
                </span>
                <div className="mt-3 flex justify-between">
                    <Button disabled={loading} text="Cancel" onClick={() => setRecipe(null)} />
                    <Popover
                        interactionKind="hover"
                        content={<div className="p-2">Cannot run a disabled segment</div>}
                        disabled={recipe?.enabled}
                    >
                        <Button
                            disabled={!recipe?.enabled}
                            text="Run Now"
                            loading={loading}
                            intent="primary"
                            onClick={() => {
                                // Use new BA runner
                                let re: Promise<any>
                                if (recipe.version == 2 && recipe.ad_type == 20) {
                                    re = axiosPost(
                                        `api/v5/recipes/${recipe?.id ?? 0}/run`,
                                        null,
                                        true,
                                    )
                                } else {
                                    re = axiosPost(`recipes/${recipe?.id ?? 0}/run`)
                                }
                                setRecipe(null)

                                re.then((res) => {
                                    dispatch(reduxSetRecipesProgress(res.data.progress))
                                }).catch((res) => {
                                    setLoading(false)
                                    Toasts.error(res.message)
                                    console.log('res', res)
                                })
                            }}
                        />
                    </Popover>
                </div>
            </Dialog>
        </Layout>
    )
}

interface RecipesProgressPreviewDialogProps {
    isOpen: boolean
    data: { [key: number]: BulkActionProgress }
    recipes: Models.Recipe[]
}

export const RecipesProgressPreviewDialog = ({
    isOpen,
    data,
    recipes,
}: RecipesProgressPreviewDialogProps) => {
    return (
        <Dialog usePortal={false} isOpen={isOpen} className="p-4">
            <RecipesProgressPreview data={data} recipes={recipes} />
        </Dialog>
    )
}

interface RecipesProgressPreviewProps {
    data: { [key: number]: BulkActionProgress }
    recipes: Models.Recipe[]
}

const RecipesProgressPreview = ({ data, recipes }: RecipesProgressPreviewProps) => {
    const bars = Object.keys(data).map((recipeId) => {
        const recipe = recipes.find((r) => r.id == parseInt(recipeId))
        const name = recipe?.name ?? null
        const progress = data[parseInt(recipeId)]

        return {
            progress,
            title: (
                <span>
                    {name}{' '}
                    <span className="text-sm text-gray-500">
                        {progress.trigger == RecipeTrigger.Manual
                            ? '(Triggered Manually)'
                            : '(Triggered Automatically)'}
                    </span>
                </span>
            ),
        }
    })

    return (
        <ProgressPreview
            bars={bars}
            description={
                <div>
                    Your segment is currently running. This can take a few minutes for large segment
                    runs
                </div>
            }
        />
    )
}

const RecipeImportModal = ({
    isOpen,
    refresh,
    setIsOpen,
}: {
    isOpen: boolean
    refresh: any
    setIsOpen: any
}) => {
    const [files, setFiles] = useState<any[] | null>(null)
    const [loading, setLoading] = useState(false)
    const [allProfiles, setAllProfiles] = useState(false)

    const fileInputText = useMemo(() => {
        if (files) {
            const parts = []
            for (let i = 0; i < files.length; i++) {
                parts.push(files[i].name)
            }
            return parts.join(', ')
        }

        return 'Choose recipe...'
    }, [files])

    useEffect(() => {
        setFiles(null)
    }, [])

    return (
        <Dialog isOpen={!!isOpen} onClose={() => setIsOpen(false)} className="p-4">
            <div className="mb-2">
                Import a previously exported segment file. You can import to either just this
                profile or to all profiles in your Merch Jar account.
                <p className="mb-0 mt-2 text-sm text-gray-600">
                    Schedules are not included in exported files. Imported segments start disabled
                    and unscheduled.
                </p>
                <Callout intent="warning" className="mt-2">
                    You can import maximum 20 segments at once
                </Callout>
            </div>

            <Switch
                checked={allProfiles}
                onChange={(e: any) => setAllProfiles(e.target.checked)}
                label="Import to all profiles"
            />

            <FileInput
                placeholder="Choose recipe..."
                disabled={loading}
                inputProps={{ accept: '.recipe', multiple: true }}
                text={fileInputText}
                onInputChange={(e: any) => {
                    setFiles(e?.target?.files)
                }}
            />

            <div>
                <Button
                    className="mt-3"
                    intent="primary"
                    loading={loading}
                    text="Import"
                    disabled={!files}
                    onClick={() => {
                        setLoading(true)
                        const formData = new FormData()
                        if (files) {
                            for (let i = 0; i < files.length; i++) {
                                formData.append('file[' + i + ']', files[i])
                            }
                        }
                        formData.append('all', allProfiles ? 'true' : 'false')
                        axiosPost(`recipes/import`, formData, false)
                            .then((r) => {
                                console.log('r is', r)
                                Toasts.success(r.data.message)
                                refresh()
                                setLoading(false)
                                setIsOpen(false)
                            })
                            .catch(() => {
                                setLoading(false)
                            })
                        console.log('upload')
                    }}
                />
            </div>
        </Dialog>
    )
}
